var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { logDetails } from "./logMessage";
import { STATEMENT_KIND_READINESS_VERIFIED, STATEMENT_KIND_VOTERS_IMPORTED } from "./logEntryDetails";
import { findingText, validFindings } from "@/services/readinessFindings";
var CHECK_KINDS = [
    "configuration",
    "certified_voters",
    "official_zero",
    "publication",
    "tested_finished",
];
var FIELD_NAMES = [
    "report",
    "passed",
    "checked_at",
    "evidence",
    "official",
    "tested",
    "official_event_id",
    "tested_event_id",
    "event_id",
    "election_id",
    "configuration",
    "external_id",
    "revision",
    "content_sha256",
    "manifest_sha256",
    "approvers",
    "areas",
    "voters",
    "import_id",
    "document_id",
    "file_name",
    "sha256",
    "declared_sha256",
    "counts_by_area",
    "imported_at",
    "voter_log_delivered",
    "current_voter_counts",
    "voter_import_in_progress",
    "status",
    "elections",
    "ballot_count",
    "received_ballot_count",
    "tally_session_count",
    "publication",
    "publication_id",
    "publication_ids",
    "matches_manifest",
    "design_count",
    "seal_at_close",
    "expected_boxes",
    "sealed_boxes",
    "all_channels_closed",
    "locked_down",
    "log_entry_id",
];
var FIELD_ALIASES = { counts_by_post: "counts_by_area", posts: "areas" };
var isObject = function (value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
};
var aliasFor = function (name) {
    return Object.prototype.hasOwnProperty.call(FIELD_ALIASES, name) ? FIELD_ALIASES[name] : undefined;
};
var canonicalField = function (name) { var _a; return (_a = aliasFor(name)) !== null && _a !== void 0 ? _a : name; };
var fieldLabel = function (t, name) {
    var canonical = canonicalField(name);
    return FIELD_NAMES.includes(canonical)
        ? String(t("logsScreen.readiness.fields.".concat(canonical)))
        : name;
};
var isReadiness = function (kind) {
    return kind === STATEMENT_KIND_VOTERS_IMPORTED || kind === STATEMENT_KIND_READINESS_VERIFIED;
};
/** Translate the view only; historical signed field names and messages stay intact. */
export var readinessDescription = function (t, kind) {
    return isReadiness(kind) ? String(t("logsScreen.readiness.description.".concat(kind))) : null;
};
/**
 * CheckKind is the stable language-independent contract, including in old
 * reports. The signed condition text stays visible beside its translation,
 * followed by any failure detail the report carries.
 */
export var readinessCheckLines = function (t, message) {
    var details = logDetails(message);
    var report = isObject(details === null || details === void 0 ? void 0 : details.report) ? details.report : details;
    if (!Array.isArray(report === null || report === void 0 ? void 0 : report.checks))
        return null;
    return report.checks.filter(isObject).flatMap(function (check) {
        var _a;
        var kind = typeof check.kind === "string" ? check.kind : String((_a = check.name) !== null && _a !== void 0 ? _a : "");
        var description = CHECK_KINDS.some(function (known) { return known === kind; })
            ? String(t("logsScreen.readiness.checks.".concat(kind)))
            : kind;
        var outcome = String(t(check.passed === true ? "logsScreen.readiness.passed" : "logsScreen.readiness.failed"));
        var line = typeof check.message === "string" && check.message.trim()
            ? t("logsScreen.readiness.checkResultWithMessage", {
                outcome: outcome,
                description: description,
                message: check.message,
                interpolation: { escapeValue: false },
            })
            : t("logsScreen.readiness.checkResult", { outcome: outcome, description: description });
        var findings = Array.isArray(check.findings) && validFindings(check.findings)
            ? check.findings.map(function (finding) { return findingText(t, finding); })
            : [];
        return __spreadArray([String(line)], findings, true);
    });
};
var shownValue = function (t, value, field) {
    if (value === null || value === undefined)
        return String(t("logsScreen.entry.values.none"));
    if (typeof value === "boolean")
        return String(t(value ? "logsScreen.entry.values.yes" : "logsScreen.entry.values.no"));
    if (Array.isArray(value))
        return value.map(function (item) { return shownValue(t, item); }).join(", ");
    if (!isObject(value))
        return String(value);
    if (field === "counts_by_area" || field === "current_voter_counts") {
        return Object.entries(value)
            .map(function (_a) {
            var name = _a[0], count = _a[1];
            return "".concat(name, ": ").concat(shownValue(t, count));
        })
            .join("; ");
    }
    return (Object.entries(value)
        // Checks have their own translated explanation; never surface old backend prose.
        .filter(function (_a) {
        var name = _a[0];
        return name !== "checks" && !(aliasFor(name) && canonicalField(name) in value);
    })
        .map(function (_a) {
        var name = _a[0], inner = _a[1];
        return "".concat(fieldLabel(t, name), ": ").concat(shownValue(t, inner, canonicalField(name)));
    })
        .join("; "));
};
/** Known receipt/report labels, with dynamic area identifiers preserved as data. */
export var readinessDetailRows = function (t, kind, message) {
    if (!isReadiness(kind))
        return null;
    var details = logDetails(message);
    if (!details)
        return null;
    return Object.entries(details)
        .filter(function (_a) {
        var name = _a[0];
        return name !== "actor" &&
            name !== "checks" &&
            !(aliasFor(name) && canonicalField(name) in details);
    })
        .map(function (_a) {
        var name = _a[0], value = _a[1];
        return ({
            name: fieldLabel(t, name),
            value: shownValue(t, value, canonicalField(name)),
        });
    });
};
